import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import * as z from 'zod'
import { useTranslation } from '@kostbar/i18n'
import type { ProductMeta } from '@/api/endpoints/meta'
import { metaApi } from '@/api/endpoints/meta'
import { externalRefsApi } from '@/api/endpoints/external-refs'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'

const allergens = [
  'gluten',
  'crustaceans',
  'eggs',
  'fish',
  'peanuts',
  'soybeans',
  'milk',
  'nuts',
  'celery',
  'mustard',
  'sesame',
  'sulphites',
  'lupin',
  'molluscs',
] as const

type AllergenKey = (typeof allergens)[number]

const TAG_KEYS = [
  'vegan',
  'vegetarian',
  'gluten_free',
  'spicy',
  'porkFree',
  'organic',
] as const

const allergenValue = z.union([
  z.literal('0'),
  z.literal('1'),
  z.literal('2'),
  z.literal('__unset'),
])

const allergensShape = allergens.reduce<Record<AllergenKey, z.ZodTypeAny>>(
  (acc, key) => {
    acc[key] = allergenValue.optional()
    return acc
  },
  {} as Record<AllergenKey, z.ZodTypeAny>,
)

const allergensSchema = z.object(allergensShape).partial()

const NUTRITION_FIELDS = [
  'energyKj',
  'energyKcal',
  'fatG',
  'saturatesG',
  'carbohydrateG',
  'sugarsG',
  'proteinG',
  'saltG',
] as const

const formSchema = z.object({
  tags: z
    .object({
      vegan: z.boolean().optional(),
      vegetarian: z.boolean().optional(),
      gluten_free: z.boolean().optional(),
      spicy: z.boolean().optional(),
      halal: z.boolean().optional(), // legacy input support
      porkFree: z.boolean().optional(),
      organic: z.boolean().optional(),
    })
    .partial(),
  // Allow sentinel "__unset" for Unknown alongside values "0" | "1" | "2".
  allergens: allergensSchema.optional(),
  nutritionPer100: z
    .object({
      energyKj: z.number().nullable().optional(),
      energyKcal: z.number().nullable().optional(),
      fatG: z.number().nullable().optional(),
      saturatesG: z.number().nullable().optional(),
      carbohydrateG: z.number().nullable().optional(),
      sugarsG: z.number().nullable().optional(),
      proteinG: z.number().nullable().optional(),
      saltG: z.number().nullable().optional(),
    })
    .partial()
    .optional(),
})

type FormValues = z.output<typeof formSchema>

export function ProductMetaTab({ sku }: { sku: string }) {
  const queryClient = useQueryClient()
  const { t: tProducts } = useTranslation('products')
  const { data } = useQuery({
    queryKey: ['product-meta', sku],
    queryFn: () => metaApi.get(sku),
  })
  const importedQuery = useQuery({
    queryKey: ['product-external-ref-webbill', 'list'],
    queryFn: () => externalRefsApi.listBySource('webbill'),
    staleTime: 30_000,
  })
  const isImported = !!importedQuery.data?.items.some(
    (it: any) => it.sku === sku,
  )
  const tagLabels: Record<(typeof TAG_KEYS)[number], string> = {
    vegan: tProducts('meta.tagVegan'),
    vegetarian: tProducts('meta.tagVegetarian'),
    gluten_free: tProducts('meta.tagGlutenFree'),
    spicy: tProducts('meta.tagSpicy'),
    porkFree: tProducts('meta.tagPorkFree'),
    organic: tProducts('meta.tagOrganic'),
  }
  const allergenLabels: Record<AllergenKey, string> = {
    gluten: tProducts('meta.allergenGluten'),
    crustaceans: tProducts('meta.allergenCrustaceans'),
    eggs: tProducts('meta.allergenEggs'),
    fish: tProducts('meta.allergenFish'),
    peanuts: tProducts('meta.allergenPeanuts'),
    soybeans: tProducts('meta.allergenSoybeans'),
    milk: tProducts('meta.allergenMilk'),
    nuts: tProducts('meta.allergenNuts'),
    celery: tProducts('meta.allergenCelery'),
    mustard: tProducts('meta.allergenMustard'),
    sesame: tProducts('meta.allergenSesame'),
    sulphites: tProducts('meta.allergenSulphites'),
    lupin: tProducts('meta.allergenLupin'),
    molluscs: tProducts('meta.allergenMolluscs'),
  }
  const nutritionLabels: Record<(typeof NUTRITION_FIELDS)[number], string> = {
    energyKj: tProducts('meta.nutritionEnergyKj'),
    energyKcal: tProducts('meta.nutritionEnergyKcal'),
    fatG: tProducts('meta.nutritionFat'),
    saturatesG: tProducts('meta.nutritionSaturates'),
    carbohydrateG: tProducts('meta.nutritionCarbs'),
    sugarsG: tProducts('meta.nutritionSugars'),
    proteinG: tProducts('meta.nutritionProtein'),
    saltG: tProducts('meta.nutritionSalt'),
  }

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: normalizeToForm(data),
  })

  useEffect(() => {
    const normalized = normalizeToForm(data)
    if (normalized) {
      form.reset(normalized)
    }
  }, [data, form])

  const mutation = useMutation({
    mutationFn: (payload: ProductMeta) => metaApi.upsert(sku, payload),
    onSuccess: () => {
      toast.success(tProducts('meta.saveSuccess'))
      queryClient.invalidateQueries({ queryKey: ['product-meta', sku] })
    },
    onError: () => toast.error(tProducts('meta.saveFailed')),
  })

  const onSubmit = (values: FormValues) => {
    if (isImported) {
      toast.info(tProducts('meta.importedReadOnly'))
      return
    }
    // Transform form back to API shape
    // Normalize tags: map legacy 'halal' into 'porkFree' and drop 'halal'.
    const normalizedTags = { ...values.tags } as any
    if (
      normalizedTags.porkFree === undefined &&
      typeof normalizedTags.halal === 'boolean'
    ) {
      normalizedTags.porkFree = normalizedTags.halal
    }
    delete normalizedTags.halal
    const payload: ProductMeta = {
      tags: normalizedTags,
      allergens: values.allergens
        ? Object.fromEntries(
            Object.entries(values.allergens).map(([k, v]) => [
              k,
              v === '__unset' ? null : (Number(v as any) as 0 | 1 | 2),
            ]),
          )
        : undefined,
      nutritionPer100: values.nutritionPer100,
    }
    mutation.mutate(payload)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {isImported && (
          <div className="rounded-md border p-3 bg-muted">
            <p className="text-sm text-muted-foreground">
              {tProducts('meta.importedNote')}
            </p>
          </div>
        )}
        <section className="space-y-3">
          <h3 className="font-semibold">{tProducts('meta.tagsTitle')}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {TAG_KEYS.map((t) => (
              <FormField
                key={t}
                control={form.control}
                name={`tags.${t}` as const}
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Checkbox
                        checked={!!field.value}
                        onCheckedChange={(v) => field.onChange(Boolean(v))}
                        disabled={isImported}
                      />
                    </FormControl>
                    <FormLabel>{tagLabels[t]}</FormLabel>
                  </FormItem>
                )}
              />
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="font-semibold">{tProducts('meta.allergensTitle')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {allergens.map((a) => (
              <FormField
                key={a}
                control={form.control}
                name={`allergens.${a}` as const}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{allergenLabels[a]}</FormLabel>
                    <Select
                      value={
                        field.value === undefined ||
                        field.value === null ||
                        field.value === ''
                          ? undefined
                          : String(field.value)
                      }
                      onValueChange={(val) => (field.onChange as any)(val)}
                      disabled={isImported}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={tProducts('meta.allergenSelect')}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__unset">
                          {tProducts('meta.allergenUnknown')}
                        </SelectItem>
                        <SelectItem value="0">
                          {tProducts('meta.allergenNone')}
                        </SelectItem>
                        <SelectItem value="1">
                          {tProducts('meta.allergenMayContain')}
                        </SelectItem>
                        <SelectItem value="2">
                          {tProducts('meta.allergenContains')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="font-semibold">{tProducts('meta.nutritionTitle')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {NUTRITION_FIELDS.map((name) => (
              <FormField
                key={name}
                control={form.control}
                name={`nutritionPer100.${name}` as const}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{nutritionLabels[name]}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="any"
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value
                          if (raw === '') {
                            field.onChange(undefined)
                            return
                          }
                          const nextValue = Number(raw)
                          field.onChange(
                            Number.isNaN(nextValue) ? undefined : nextValue,
                          )
                        }}
                        disabled={isImported}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </section>

        <div className="flex justify-end">
          <Button type="submit" disabled={mutation.isPending || isImported}>
            {tProducts('meta.save')}
          </Button>
        </div>
      </form>
    </Form>
  )
}

function normalizeToForm(meta?: ProductMeta): FormValues | undefined {
  if (!meta) return undefined
  const allergensEntries = Object.entries(meta.allergens ?? {}).map(
    ([k, v]) => [k, v == null ? '__unset' : String(v)],
  )
  // Map legacy 'halal' to 'porkFree' for display
  const tags = { ...(meta.tags ?? {}) } as any
  if (tags.porkFree === undefined && typeof tags.halal === 'boolean') {
    tags.porkFree = tags.halal
  }
  delete tags.halal
  return {
    tags,
    allergens: allergensEntries.length
      ? Object.fromEntries(allergensEntries)
      : undefined,
    nutritionPer100: meta.nutritionPer100 ?? {},
  } as any
}
